import {
  Bubble, Color, GameState, NUM_COLORS, START_ROWS, DROP_INTERVAL,
  DEATH_LINE_Y, LAUNCHER_X, AIM_MIN, AIM_MAX,
} from './types';
import { key, neighbors, colsInRow, cellCenterY, effParity } from './grid';

function randInt(n: number): number { return (Math.random() * n) | 0; }

export function occupiedSet(bubbles: Map<string, Bubble>): Set<string> {
  return new Set(bubbles.keys());
}

export function colorsOnField(bubbles: Map<string, Bubble>): Color[] {
  const set = new Set<Color>();
  for (const b of bubbles.values()) set.add(b.color);
  return [...set];
}

// Loaded/preview colour comes only from colours present on the field (no dead colours).
export function pickColor(bubbles: Map<string, Bubble>): Color {
  const present = colorsOnField(bubbles);
  if (present.length === 0) return randInt(NUM_COLORS);
  return present[randInt(present.length)];
}

// Flood-fill connected same-colour group (incl. the seed).
function floodMatch(
  bubbles: Map<string, Bubble>, row: number, col: number, color: Color, dropCount: number,
): string[] {
  const seen = new Set<string>([key(row, col)]);
  const stack: [number, number][] = [[row, col]];
  const out: string[] = [key(row, col)];
  while (stack.length) {
    const [r, c] = stack.pop()!;
    for (const [nr, nc] of neighbors(r, c, dropCount)) {
      const k = key(nr, nc);
      if (seen.has(k)) continue;
      const nb = bubbles.get(k);
      if (nb && nb.color === color) {
        seen.add(k); out.push(k); stack.push([nr, nc]);
      }
    }
  }
  return out;
}

// Bubbles NOT connected (any colour) to a ceiling-anchored bubble (row 0).
function findFloating(bubbles: Map<string, Bubble>, dropCount: number): string[] {
  const anchored = new Set<string>();
  const stack: [number, number][] = [];
  for (const b of bubbles.values()) {
    if (b.row === 0) { const k = key(b.row, b.col); anchored.add(k); stack.push([b.row, b.col]); }
  }
  while (stack.length) {
    const [r, c] = stack.pop()!;
    for (const [nr, nc] of neighbors(r, c, dropCount)) {
      const k = key(nr, nc);
      if (anchored.has(k)) continue;
      if (bubbles.has(k)) { anchored.add(k); stack.push([nr, nc]); }
    }
  }
  const floating: string[] = [];
  for (const k of bubbles.keys()) if (!anchored.has(k)) floating.push(k);
  return floating;
}

// Puzzle-Bobble drop bonus: 20, 40, 80 … doubling, capped per bubble at 10240.
function dropBonus(n: number): number {
  let total = 0;
  for (let i = 0; i < n; i++) total += Math.min(20 * 2 ** i, 10240);
  return total;
}

export interface ShotResult {
  bubbles: Map<string, Bubble>;
  scoreGained: number;
  popped: Bubble[];   // matched group that burst (empty if no match)
  dropped: Bubble[];  // detached bubbles that fell
}

// Attach the snapped bubble, then resolve match + floating drop. Pure.
export function applyShot(
  bubbles: Map<string, Bubble>, row: number, col: number, color: Color, dropCount: number,
): ShotResult {
  const next = new Map(bubbles);
  next.set(key(row, col), { row, col, color });

  const group = floodMatch(next, row, col, color, dropCount);
  if (group.length < 3) {
    return { bubbles: next, scoreGained: 0, popped: [], dropped: [] };
  }

  const popped: Bubble[] = group.map(k => next.get(k)!);
  for (const k of group) next.delete(k);
  let score = group.length * 10;

  const floatKeys = findFloating(next, dropCount);
  const dropped: Bubble[] = floatKeys.map(k => next.get(k)!);
  for (const k of floatKeys) next.delete(k);
  score += dropBonus(dropped.length);

  return { bubbles: next, scoreGained: score, popped, dropped };
}

// Insert a fresh random top row and push everything down one row.
export function dropCeiling(state: GameState): { bubbles: Map<string, Bubble>; dropCount: number } {
  const dropCount = state.dropCount + 1;
  const moved = new Map<string, Bubble>();
  for (const b of state.bubbles.values()) {
    const nb = { ...b, row: b.row + 1 };
    moved.set(key(nb.row, nb.col), nb);
  }
  const cols = colsInRow(0, dropCount);
  for (let c = 0; c < cols; c++) {
    moved.set(key(0, c), { row: 0, col: c, color: randInt(NUM_COLORS) });
  }
  return { bubbles: moved, dropCount };
}

export function isLost(bubbles: Map<string, Bubble>): boolean {
  for (const b of bubbles.values()) {
    if (cellCenterY(b.row) >= DEATH_LINE_Y) return true;
  }
  return false;
}

export function isWin(bubbles: Map<string, Bubble>): boolean {
  return bubbles.size === 0;
}

export function createInitialBubbles(): Map<string, Bubble> {
  const bubbles = new Map<string, Bubble>();
  for (let row = 0; row < START_ROWS; row++) {
    const cols = colsInRow(row, 0);
    for (let col = 0; col < cols; col++) {
      bubbles.set(key(row, col), { row, col, color: randInt(NUM_COLORS) });
    }
  }
  return bubbles;
}

export function createState(best: number): GameState {
  const bubbles = createInitialBubbles();
  return {
    bubbles,
    dropCount: 0,
    dropCounter: DROP_INTERVAL,
    current: pickColor(bubbles),
    next: pickColor(bubbles),
    projectile: null,
    aimAngle: Math.PI / 2,
    score: 0,
    best,
    phase: 'menu',
  };
}

export function clampAim(angle: number): number {
  return Math.max(AIM_MIN, Math.min(AIM_MAX, angle));
}

// Angle (radians, π/2 = up) from launcher toward a point.
export function aimToward(px: number, py: number, launcherY: number): number {
  const dx = px - LAUNCHER_X;
  const dy = launcherY - py;            // up is positive
  return clampAim(Math.atan2(dy, dx));
}

// Re-export so callers don't need a second import for parity-aware widths.
export { colsInRow, effParity };
